import React, { useState, MutableRefObject } from 'react';
import { Box, Typography, Tooltip, Button } from '@mui/material';
import { Add, Receipt } from '@mui/icons-material';
import IosShareIcon from '@mui/icons-material/IosShare';
import WhiteFab from '../common/WhiteFab';
import CreateInvoice from './CreateInvoice';
import PastInvoices from './PastInvoices';
import TotalBalanceDue from './TotalBalanceDue';
import { BaseInvoiceDTO, CreateInvoiceDTO, ProjectData, InvoiceMode, EditInvoiceValues } from './invoice.type';
import { ReportInvoiceDTO } from '../monitoring-reports/shared';

export interface InvoiceRendererProps<T extends BaseInvoiceDTO> {
  invoices: T[];
  onAddInvoice: (invoice: CreateInvoiceDTO) => void | Promise<void>;
  onDeleteInvoice: (invoiceId: string) => void;
  onUpdatePaidAmount?: (invoiceId: string, paidAmount: number) => Promise<void>;
  onEditInvoice?: (invoiceId: string, values: EditInvoiceValues) => Promise<void>;
  projectData?: ProjectData;
  isExportMode?: boolean;
  onExportInvoice?: (invoice: CreateInvoiceDTO) => void;
  onExportPastInvoice?: (invoice: CreateInvoiceDTO) => void;
  onExportAllInvoices?: (invoices: ReportInvoiceDTO[]) => void;
  title?: string;
  service?: string;
  isMonitoringReport?: boolean;
  currentInvoice?: T;
  mode?: InvoiceMode;
  saveRef?: MutableRefObject<(() => Promise<boolean>) | null>;
  completeToggle?: React.ReactNode;
}

const InvoiceRenderer = <T extends BaseInvoiceDTO>({
  invoices = [],
  onAddInvoice,
  onDeleteInvoice,
  onUpdatePaidAmount,
  onEditInvoice,
  projectData,
  isExportMode = false,
  onExportInvoice,
  onExportPastInvoice,
  onExportAllInvoices,
  title = 'Invoices',
  service = '',
  isMonitoringReport = false,
  currentInvoice,
  mode = 'regular',
  saveRef,
  completeToggle,
}: InvoiceRendererProps<T>) => {
  const [isAddingNew, setIsAddingNew] = useState(isExportMode || (isMonitoringReport && !currentInvoice));

  const handleAddNew = () => {
    setIsAddingNew(true);
  };

  const handleCancelNew = () => {
    if (!isExportMode && !isMonitoringReport) {
      setIsAddingNew(false);
    }
  };

  const handleSaveInvoice = async (invoiceData: CreateInvoiceDTO) => {
    const isCurrentInvoice = (invoiceData as any).currentInvoice;
    if (isCurrentInvoice) {
      // Save directly to backend
    } else {
      // Add to state for save draft
    }
    await onAddInvoice(invoiceData);
    if (!isMonitoringReport || mode === 'past') {
      setIsAddingNew(false);
    }
  };

  const handleAddPastInvoice = (invoiceData: CreateInvoiceDTO) => {
    onAddInvoice({
      ...invoiceData,
      currentInvoice: false,
    } as CreateInvoiceDTO);
  };

  const handleExportAllInvoices = () => {
    if (onExportAllInvoices) {
      onExportAllInvoices(invoices as unknown as ReportInvoiceDTO[]);
    }
  };

  // Check if export all button should be enabled
  const canExportAll = isMonitoringReport && currentInvoice && onExportAllInvoices;

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant='h5' sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {completeToggle ?? <Receipt color='primary' />}
          {title}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {canExportAll && (
            <Tooltip title='Export all invoices as PDF'>
              <Button onClick={handleExportAllInvoices} size='small' startIcon={<IosShareIcon />} sx={{ minWidth: 'auto' }}>
                Export Invoice
              </Button>
            </Tooltip>
          )}
          {!isAddingNew && !isExportMode && !isMonitoringReport && (
            <Tooltip title='Add New Invoice'>
              <WhiteFab size='small' icon={<Add fontSize='small' />} onClick={handleAddNew} textColor='primary' />
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Current/New Invoice Form */}
      {(isAddingNew || isExportMode || isMonitoringReport) && (
        <CreateInvoice
          projectData={projectData ? ({ ...projectData, invoices } as ProjectData) : ({ invoices } as ProjectData)}
          isExportMode={isExportMode}
          onSaveInvoice={handleSaveInvoice}
          onExportInvoice={onExportInvoice}
          onCancel={handleCancelNew}
          service={service}
          isMonitoringReport={isMonitoringReport}
          currentInvoice={currentInvoice}
          mode={mode}
          saveRef={saveRef}
        />
      )}

      {/* Past Invoices */}
      <PastInvoices
        invoices={invoices}
        onDeleteInvoice={onDeleteInvoice}
        isExportMode={isExportMode}
        isMonitoringReport={isMonitoringReport}
        onAddPastInvoice={isMonitoringReport ? handleAddPastInvoice : undefined}
        onExportInvoice={onExportPastInvoice}
        onUpdatePaidAmount={onUpdatePaidAmount}
        onEditInvoice={onEditInvoice}
        projectData={projectData}
        service={service}
      />

      {/* Total Balance Due - only for monitoring reports */}
      {isMonitoringReport && <TotalBalanceDue invoices={invoices} currentInvoice={currentInvoice} />}
    </Box>
  );
};

export default InvoiceRenderer;
